import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { SettingsContext } from '../../contexts/SettingsContext';
import {
  useCalendarData,
  useClientsData,
  usePackagesData,
  useSuppliersData,
  useFinancialData
} from '../../services/dataService';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  Button,
  TextField,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Divider,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Badge,
  Tooltip,
  Fab,
  Tabs,
  Tab,
  Autocomplete,
  LinearProgress,
  Stack,
  AvatarGroup
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  Add as AddIcon,
  Event as EventIcon,
  FlightTakeoff as FlightIcon,
  FlightLand as LandingIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  CalendarMonth as MonthIcon,
  CalendarViewWeek as WeekIcon,
  CalendarViewDay as DayIcon,
  Notifications as NotificationIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Check as CheckIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Star as StarIcon,
  Analytics as AnalyticsIcon,
  Link as LinkIcon,
  TrendingUp as TrendingUpIcon,
  Info as InfoIcon,
  MonetizationOn as MoneyIcon,
  Group as GroupIcon,
  Flight as FlightMainIcon,
  Hotel as HotelIcon,
  Restaurant as RestaurantIcon,
  LocalActivity as ActivityIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  CalendarToday as CalendarTodayIcon,
  EventAvailable as EventAvailableIcon,
  EventBusy as EventBusyIcon,
  School as SchoolIcon,
  ViewList as ViewListIcon
} from '@mui/icons-material';

// Simple Chart Components
const SimpleEventChart = ({ data, title }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PieChartIcon color="primary" />
          {title}
        </Typography>
        <Box sx={{ mt: 2 }}>
          {data.map((item, index) => (
            <Box key={index} sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">{item.label}</Typography>
                <Typography variant="body2" fontWeight={600}>{item.value}</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(item.value / total) * 100}
                sx={{ height: 8, borderRadius: 1 }}
                color={item.color || 'primary'}
              />
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

const SimpleActivityChart = ({ activities }) => {
  const days = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  const maxValue = Math.max(...activities);
  
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BarChartIcon color="primary" />
          Activité Hebdomadaire
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'end', gap: 1, height: 100, mt: 2 }}>
          {activities.map((value, index) => (
            <Box key={index} sx={{ flex: 1, textAlign: 'center' }}>
              <Box
                sx={{
                  height: `${(value / maxValue) * 80}px`,
                  backgroundColor: 'primary.main',
                  borderRadius: 1,
                  mb: 1,
                  minHeight: 4
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {days[index]}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

const Calendrier = () => {
  const { settings, formatCurrency, formatDate, calculateTotalWithTax } = useContext(SettingsContext);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [selectedDate, setSelectedDate] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [filterType, setFilterType] = useState('all');

  // Use real-time interconnected data hooks
  const { events, loading: eventsLoading, error: eventsError, stats: eventsStats } = useCalendarData({
    eventType: filterType !== 'all' ? filterType : undefined,
    startDate: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).toISOString().split('T')[0]
  });
  const { clients, loading: clientsLoading } = useClientsData();
  const { packages, loading: packagesLoading } = usePackagesData();
  const { suppliers, loading: suppliersLoading } = useSuppliersData();
  const { financialData, loading: financialLoading, error: financialError } = useFinancialData();
  
  // Combined loading state
  const isLoading = eventsLoading || clientsLoading || packagesLoading || suppliersLoading || financialLoading;

  const [formData, setFormData] = useState({
    titre: '',
    type: 'Professionnel',
    date: '',
    heure: '',
    client: null,
    package: null,
    destination: '',
    statut: 'Planifié',
    description: '',
    participants: 1,
    revenue: 0
  });

  // Get events for a specific date
  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(event => event.date === dateStr);
  };

  // Get events for current month
  const getEventsForMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.getFullYear() === year && eventDate.getMonth() === month;
    });
  };

  // Navigate calendar
  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    // This will return an array of date objects for the calendar grid
    const days = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      days.push(date);
      startDate.setDate(startDate.getDate() + 1);
    }
    return days;
  };

  // Open event dialog
  const handleOpenDialog = (event = null, date = null) => {
    if (event) {
      // Edit existing event
      setEditingEvent(event);
      
      // Find related client and package for pre-selection
      const relatedClient = event.client ? clients.find(c => c.id === event.client.id) : null;
      const relatedPackage = event.package ? packages.find(p => p.id === event.package.id) : null;
      
      setSelectedClient(relatedClient);
      setSelectedPackage(relatedPackage);
      
      setFormData({
        titre: event.titre,
        type: event.type,
        date: event.date,
        heure: event.heure,
        client: relatedClient,
        package: relatedPackage,
        destination: event.destination,
        statut: event.statut,
        description: event.description,
        participants: event.participants,
        revenue: event.revenue
      });
    } else {
      // Create new event
      setEditingEvent(null);
      
      // Use the clicked date if available
      const selectedDate = date ? date.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
      
      setSelectedClient(null);
      setSelectedPackage(null);
      
      setFormData({
        titre: '',
        type: 'Professionnel',
        date: selectedDate,
        heure: '09:00',
        client: null,
        package: null,
        destination: '',
        statut: 'Planifié',
        description: '',
        participants: 1,
        revenue: 0
      });
    }
    setOpenDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEvent(null);
    setSelectedClient(null);
    setSelectedPackage(null);
  };

  // Save event
  const handleSaveEvent = () => {
    try {
      // Validate required fields
      if (!formData.titre || !formData.date) {
        setSnackbar({
          open: true,
          message: 'Veuillez remplir tous les champs obligatoires',
          severity: 'error'
        });
        return;
      }

      // Get existing events from localStorage
      const existingEvents = JSON.parse(localStorage.getItem('calendarEvents') || '[]');
      
      // Create a new event object with all necessary data
      const newEvent = {
        id: editingEvent ? editingEvent.id : `EVT-${Date.now()}`,
        titre: formData.titre,
        type: formData.type,
        date: formData.date,
        heure: formData.heure,
        client: formData.client,
        package: formData.package,
        destination: formData.destination,
        statut: formData.statut,
        description: formData.description,
        participants: formData.participants,
        revenue: formData.revenue,
        couleur: getCouleurByType(formData.type),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Update or add the event
      let updatedEvents;
      if (editingEvent) {
        // Update existing event
        updatedEvents = existingEvents.map(event => 
          event.id === editingEvent.id ? newEvent : event
        );
      } else {
        // Add new event
        updatedEvents = [...existingEvents, newEvent];
      }

      // Save to localStorage
      localStorage.setItem('calendarEvents', JSON.stringify(updatedEvents));
      
      // Invalidate cache to force reload
      localStorage.removeItem('dataCache');
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('calendarDataUpdate', {
        detail: { events: updatedEvents }
      }));
      
      // Show success message
      setSnackbar({
        open: true,
      message: editingEvent ? 'Événement modifié avec succès' : 'Nouvel événement ajouté',
        severity: 'success'
      });

    // Close the dialog
    handleCloseDialog();
    } catch (error) {
      console.error('Error saving event:', error);
      setSnackbar({
        open: true,
        message: 'Une erreur est survenue lors de l\'enregistrement de l\'événement',
        severity: 'error'
      });
    }
  };

  // Delete event
  const handleDeleteEvent = () => {
    try {
      if (!editingEvent) return;

      // Get existing events from localStorage
      const existingEvents = JSON.parse(localStorage.getItem('calendarEvents') || '[]');
      
      // Filter out the event to delete
      const updatedEvents = existingEvents.filter(event => event.id !== editingEvent.id);
      
      // Save to localStorage
      localStorage.setItem('calendarEvents', JSON.stringify(updatedEvents));
      
      // Invalidate cache to force reload
      localStorage.removeItem('dataCache');
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('calendarDataUpdate', {
        detail: { events: updatedEvents }
      }));
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Événement supprimé avec succès',
        severity: 'success'
      });

      // Close the dialog
      handleCloseDialog();
    } catch (error) {
      console.error('Error deleting event:', error);
      setSnackbar({
        open: true,
        message: 'Une erreur est survenue lors de la suppression de l\'événement',
        severity: 'error'
      });
    }
  };

  // Get color for event type
  const getCouleurByType = (type) => {
    switch (type) {
      case 'Départ':
        return 'success';
      case 'Retour':
        return 'primary';
      case 'Professionnel':
        return 'info';
      case 'Formation':
        return 'secondary';
      case 'Événement':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get icon for event type
  const getTypeIcon = (type) => {
    switch (type) {
      case 'Départ':
        return <FlightIcon sx={{ fontSize: 16 }} />;
      case 'Retour':
        return <LandingIcon sx={{ fontSize: 16 }} />;
      case 'Professionnel':
        return <BusinessIcon sx={{ fontSize: 16 }} />;
      case 'Formation':
        return <SchoolIcon sx={{ fontSize: 16 }} />;
      case 'Événement':
        return <EventIcon sx={{ fontSize: 16 }} />;
      default:
        return <EventIcon sx={{ fontSize: 16 }} />;
    }
  };

  const calendarDays = generateCalendarDays();
  const monthEvents = getEventsForMonth();

  // Enhanced statistics for real-time data
  const statsCards = React.useMemo(() => {
    if (isLoading) {
      return [
        { title: 'Événements du Mois', value: 0, icon: EventIcon, color: 'primary', trend: '-', subtitle: '0 à venir' },
        { title: 'Clients Voyageant', value: 0, icon: PersonIcon, color: 'info', trend: '-', subtitle: 'Clients actifs' },
        { title: 'Revenu Estimé', value: formatCurrency(0), icon: MoneyIcon, color: 'success', trend: '-', subtitle: 'Ce mois' },
        { title: 'Destinations', value: 0, icon: LocationIcon, color: 'warning', trend: '-', subtitle: 'Pays desservis' }
      ];
    }
    
    // Calculate clients with upcoming trips
    const clientsWithTrips = [...new Set(events
      .filter(e => e.client && e.type === 'Départ' && new Date(e.date) >= new Date())
      .map(e => e.client.id)
    )].length;
    
    // Calculate total revenue from events this month
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const monthlyRevenue = events
      .filter(e => {
        const eventDate = new Date(e.date);
        return eventDate.getMonth() === currentMonth && eventDate.getFullYear() === currentYear;
      })
      .reduce((sum, e) => sum + (e.revenue || 0), 0);
    
    // Calculate unique destinations
    const uniqueDestinations = [...new Set(events
      .filter(e => e.destination)
      .map(e => {
        const parts = e.destination.split(',');
        return parts.length > 1 ? parts[1].trim() : parts[0].trim();
      })
    )];
    
    // Calculate revenue trend compared to previous month
    const previousMonthRevenue = financialData
      ? financialData
          .filter(t => {
            const transDate = new Date(t.date);
            const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
            const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
            return transDate.getMonth() === prevMonth && transDate.getFullYear() === prevYear;
          })
          .reduce((sum, t) => sum + (t.montant || 0), 0)
      : 0;
    
    const revenueTrend = previousMonthRevenue > 0 
      ? `${Math.round((monthlyRevenue / previousMonthRevenue - 1) * 100)}%`
      : '+N/A';
    
    return [
      {
        title: 'Événements du Mois',
        value: eventsStats?.totalEvents || 0,
        icon: EventIcon,
      color: 'primary',
        trend: `+${Math.round(eventsStats?.upcomingEvents / (eventsStats?.totalEvents || 1) * 100)}%`,
        subtitle: `${eventsStats?.upcomingEvents || 0} à venir`
      },
      {
        title: 'Clients Voyageant',
        value: clientsWithTrips,
        icon: PersonIcon,
        color: 'info',
        trend: `${Math.round(clientsWithTrips / (clients.length || 1) * 100)}%`,
        subtitle: 'Clients actifs'
      },
      {
        title: 'Revenu Estimé',
        value: formatCurrency(monthlyRevenue),
        icon: MoneyIcon,
        color: 'success',
        trend: revenueTrend,
        isCurrency: true,
        subtitle: 'Ce mois'
      },
      {
        title: 'Destinations',
        value: uniqueDestinations.length,
        icon: LocationIcon,
      color: 'warning',
        trend: `+${uniqueDestinations.length - 3}`,
        subtitle: 'Pays desservis'
    }
  ];
  }, [events, eventsStats, clients, financialData, isLoading, formatCurrency]);

  // Data for charts based on real-time data
  const eventTypeData = [
    { label: 'Départs', value: eventsStats?.eventsByType?.['Départ'] || 0, color: 'success' },
    { label: 'Retours', value: eventsStats?.eventsByType?.['Retour'] || 0, color: 'primary' },
    { label: 'Professionnels', value: eventsStats?.eventsByType?.['Professionnel'] || 0, color: 'info' },
    { label: 'Formations', value: eventsStats?.eventsByType?.['Formation'] || 0, color: 'secondary' },
    { label: 'Événements', value: eventsStats?.eventsByType?.['Événement'] || 0, color: 'error' }
  ].filter(item => item.value > 0);
  
  // Revenue data by destination
  const revenueData = React.useMemo(() => {
    if (isLoading || !events || events.length === 0) {
      return [];
    }
    
    return Object.entries(events
      .reduce((acc, event) => {
        if (event.revenue > 0 && event.destination) {
          const destination = event.destination.split(',')[0].trim();
          acc[destination] = (acc[destination] || 0) + event.revenue;
        }
        return acc;
      }, {}))
      .map(([key, value]) => ({ 
        label: key, 
        value: value, 
        color: ['primary', 'success', 'info', 'warning', 'error'][Math.floor(Math.random() * 5)] 
      }))
      .slice(0, 5);
  }, [events, isLoading]);

  // Weekly activity data - use real events
  const weeklyActivityData = React.useMemo(() => {
    if (isLoading || !events || events.length === 0) {
      return Array(7).fill(0);
    }
    
    // Get current week dates
    const today = new Date();
    const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const weekDays = Array(7).fill(0).map((_, i) => {
      const date = new Date(today);
      // Adjust to get Monday as first day (0)
      const dayDiff = i - ((currentDay || 7) - 1);
      date.setDate(date.getDate() + dayDiff);
      return date.toISOString().split('T')[0];
    });
    
    // Count events for each day of the week
    return weekDays.map(dateStr => 
      events.filter(e => e.date === dateStr).length
    );
  }, [events, isLoading]);

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Calendrier</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Chargement des données en temps réel...
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  // Error state
  if (eventsError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Une erreur est survenue lors du chargement des données : {eventsError}
        </Alert>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Réessayer
        </Button>
      </Box>
    );
  }

  const renderTabContent = () => {
    switch (selectedTab) {
      case 0:
        return (
          <Grid container spacing={3}>
            {/* Calendar Section */}
            <Grid item xs={12} lg={8}>
              <Card 
                sx={{ 
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: 3,
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
              >
                <CardContent>
                  {/* Calendar Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <IconButton onClick={() => navigateMonth(-1)}>
                        <ChevronLeftIcon />
                      </IconButton>
                      <Typography variant="h5" sx={{ fontWeight: 600, minWidth: 200, textAlign: 'center' }}>
                        {currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                      </Typography>
                      <IconButton onClick={() => navigateMonth(1)}>
                        <ChevronRightIcon />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={<TodayIcon />}
                        onClick={goToToday}
                        sx={{ borderRadius: 2 }}
                      >
                        Aujourd'hui
                      </Button>
                      <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(e, newMode) => newMode && setViewMode(newMode)}
                        size="small"
                      >
                        <ToggleButton value="month">
                          <MonthIcon />
                        </ToggleButton>
                        <ToggleButton value="list">
                          <ViewListIcon />
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  </Box>

                  {/* Calendar View - Month or List */}
                  {viewMode === 'month' ? (
                  <Box>
                    {/* Days of week header */}
                    <Grid container>
                      {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map((day) => (
                        <Grid item xs={12/7} key={day}>
                          <Box 
                            sx={{ 
                              p: 1, 
                              textAlign: 'center',
                              fontWeight: 600,
                              color: 'text.secondary',
                              borderBottom: '1px solid',
                              borderColor: 'divider'
                            }}
                          >
                            {day}
                          </Box>
                        </Grid>
                      ))}
                    </Grid>

                    {/* Calendar days */}
                    <Grid container>
                      {calendarDays.map((day, index) => {
                        const dayEvents = getEventsForDate(day);
                        const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                        const isToday = day.toDateString() === new Date().toDateString();
                        const hasRevenue = dayEvents.some(e => e.revenue > 0);
                        
                        return (
                          <Grid item xs={12/7} key={index}>
                            <Paper
                              elevation={0}
                              sx={{
                                minHeight: 120,
                                p: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: isCurrentMonth 
                                  ? (isToday ? 'primary.50' : 'transparent')
                                  : 'grey.50',
                                cursor: 'pointer',
                                position: 'relative',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: 'primary.50',
                                  transform: 'scale(1.02)'
                                }
                              }}
                              onClick={() => handleOpenDialog(null, day)}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: isToday ? 700 : 400,
                                    color: isCurrentMonth ? 'text.primary' : 'text.disabled'
                                  }}
                                >
                                  {day.getDate()}
                                </Typography>
                                {dayEvents.length > 0 && (
                                  <Stack direction="row" spacing={0.5}>
                                    <Badge badgeContent={dayEvents.length} color="primary" />
                                    {hasRevenue && (
                                      <MoneyIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                    )}
                                  </Stack>
                                )}
                              </Box>
                              
                              {/* Event indicators */}
                              <Box sx={{ mt: 1 }}>
                                {dayEvents.slice(0, 3).map((event, idx) => (
                                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                    {event.client && (
                                      <Avatar 
                                        sx={{ width: 16, height: 16, mr: 0.5 }}
                                        src={event.client.avatar}
                                        >
                                          {event.client.prenom?.charAt(0)}
                                        </Avatar>
                                    )}
                                    <Chip
                                      label={event.titre}
                                      size="small"
                                        color={getCouleurByType(event.type)}
                                      sx={{ 
                                        maxWidth: '100%',
                                        height: 18,
                                        fontSize: '0.65rem',
                                        '& .MuiChip-label': {
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap'
                                        }
                                      }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenDialog(event);
                                      }}
                                    />
                                  </Box>
                                ))}
                                {dayEvents.length > 3 && (
                                  <Typography variant="caption" color="text.secondary">
                                    +{dayEvents.length - 3} autres
                                  </Typography>
                                )}
                              </Box>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                  ) : (
                    // List View
                    <Box>
                      <List sx={{ width: '100%' }}>
                        {monthEvents.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body1" color="text.secondary">
                              Aucun événement ce mois-ci
                            </Typography>
                            <Button 
                              variant="contained" 
                              startIcon={<AddIcon />} 
                              sx={{ mt: 2 }}
                              onClick={() => handleOpenDialog()}
                            >
                              Ajouter un événement
                            </Button>
                          </Box>
                        ) : (
                          monthEvents
                            .sort((a, b) => new Date(a.date) - new Date(b.date))
                            .map((event, index) => {
                              const eventDate = new Date(event.date);
                              const isToday = eventDate.toDateString() === new Date().toDateString();
                              const isPast = eventDate < new Date();
                              
                              return (
                                <React.Fragment key={event.id || index}>
                                  <ListItem 
                                    sx={{ 
                                      px: 2, 
                                      py: 1.5,
                                      borderLeft: '4px solid',
                                      borderLeftColor: `${event.couleur || getCouleurByType(event.type)}.main`,
                                      mb: 1,
                                      borderRadius: 1,
                                      bgcolor: isToday ? 'primary.50' : 
                                              isPast ? 'grey.50' : 'transparent',
                                      '&:hover': {
                                        bgcolor: 'rgba(25, 118, 210, 0.08)'
                                      }
                                    }}
                                    secondaryAction={
                                      <IconButton edge="end" onClick={() => handleOpenDialog(event)}>
                                        <EditIcon />
                                      </IconButton>
                                    }
                                  >
                                    <ListItemAvatar>
                                      <Avatar 
                                        sx={{ 
                                          bgcolor: `${event.couleur || getCouleurByType(event.type)}.main` 
                                        }}
                                      >
                                        {getTypeIcon(event.type)}
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography variant="subtitle2">
                                            {event.titre}
                                          </Typography>
                                          {event.client && (
                                            <Chip 
                                              label={`${event.client.prenom} ${event.client.nom}`} 
                                              size="small" 
                                              variant="outlined"
                                            />
                                          )}
                                        </Box>
                                      }
                                      secondary={
                                        <Box>
                                          <Typography variant="body2" color="text.secondary">
                                            {formatDate(eventDate)} • {event.heure} • {event.destination}
                                          </Typography>
                                          {event.revenue > 0 && (
                                            <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                                              {formatCurrency(event.revenue)}
                                            </Typography>
                                          )}
                                        </Box>
                                      }
                                    />
                                  </ListItem>
                                </React.Fragment>
                              );
                            })
                        )}
                      </List>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Events Sidebar */}
            <Grid item xs={12} lg={4}>
              <Card 
                sx={{ 
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: 3,
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  mb: 3
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Événements à venir
                    </Typography>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog()}
                      sx={{ 
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'primary.dark' }
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                  
                  <List>
                    {events
                      .filter(event => new Date(event.date) >= new Date())
                      .sort((a, b) => new Date(a.date) - new Date(b.date))
                      .slice(0, 6)
                      .map((event, index) => (
                      <React.Fragment key={event.id}>
                        <ListItem 
                          sx={{ 
                            px: 0,
                            '&:hover': { 
                              backgroundColor: 'rgba(30, 64, 175, 0.04)',
                              borderRadius: 2
                            }
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar 
                              sx={{ 
                                bgcolor: `${event.couleur || getCouleurByType(event.type)}.main`,
                                width: 40,
                                height: 40
                              }}
                            >
                              {getTypeIcon(event.type)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {event.titre}
                                </Typography>
                                {event.client && (
                                  <Avatar 
                                    sx={{ width: 20, height: 20 }}
                                    src={event.client.avatar}
                                  >
                                    {event.client.prenom?.charAt(0)}
                                  </Avatar>
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(new Date(event.date))} à {event.heure}
                                </Typography>
                                <br />
                                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                  <Chip
                                    label={event.statut}
                                    size="small"
                                    color={event.couleur || getCouleurByType(event.type)}
                                    variant="outlined"
                                    sx={{ borderRadius: 2 }}
                                  />
                                  {event.revenue > 0 && (
                                    <Chip
                                      label={formatCurrency(event.revenue)}
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      sx={{ borderRadius: 2 }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            }
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(event)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingEvent(event);
                              handleDeleteEvent();
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItem>
                        {index < 5 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <SimpleEventChart data={eventTypeData} title="Répartition des Événements" />
            </Grid>
            <Grid item xs={12} md={4}>
              <SimpleEventChart data={revenueData} title="Revenus par Destination" />
            </Grid>
            <Grid item xs={12} md={4}>
              <SimpleActivityChart activities={weeklyActivityData} />
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Interconnect status */}
      <Box sx={{ mb: 3 }}>
        <Paper sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.04)', borderRadius: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkIcon color="primary" />
            <Typography variant="subtitle2" color="primary">
              Calendrier Interconnecté Actif
            </Typography>
          <Chip 
            label="Connecté" 
            size="small"
              color="success"
              sx={{ ml: 1 }}
          />
        </Box>
          <Typography variant="caption" color="text.secondary">
            Synchronisation en temps réel avec {events.length} événements, {clients.length} clients, et {packages.length} forfaits
          </Typography>
        </Paper>
        
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Calendrier Intelligent
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Planification avancée avec données clients et revenus en temps réel
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {statsCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {stat.subtitle}
                    </Typography>
                    <Chip 
                      label={stat.trend} 
                      size="small" 
                      color={stat.color}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  <Avatar 
                    sx={{ 
                      bgcolor: `${stat.color}.main`,
                      width: 56,
                      height: 56
                    }}
                  >
                    <stat.icon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs Navigation */}
      <Card sx={{ mb: 3 }}>
        <Tabs 
          value={selectedTab} 
          onChange={(e, newValue) => setSelectedTab(newValue)}
          variant="fullWidth"
        >
          <Tab 
            icon={<CalendarTodayIcon />} 
            label="Calendrier" 
            iconPosition="start"
          />
          <Tab 
            icon={<AssessmentIcon />} 
            label="Analytiques" 
            iconPosition="start"
          />
        </Tabs>
      </Card>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        onClick={() => handleOpenDialog()}
        sx={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)'
        }}
      >
        <AddIcon />
      </Fab>

      {/* Enhanced Add/Edit Event Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {editingEvent ? 'Modifier l\'événement' : 'Nouvel événement'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Titre de l'événement"
                value={formData.titre}
                onChange={(e) => setFormData({...formData, titre: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type d'événement</InputLabel>
                <Select
                  value={formData.type}
                  label="Type d'événement"
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="Départ">Départ</MenuItem>
                  <MenuItem value="Retour">Retour</MenuItem>
                  <MenuItem value="Professionnel">Professionnel</MenuItem>
                  <MenuItem value="Formation">Formation</MenuItem>
                  <MenuItem value="Événement">Événement</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Heure"
                type="time"
                value={formData.heure}
                onChange={(e) => setFormData({...formData, heure: e.target.value})}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                value={selectedClient}
                onChange={(event, newValue) => {
                  setSelectedClient(newValue);
                  setFormData({...formData, client: newValue});
                }}
                options={clients || []}
                getOptionLabel={(option) => `${option.prenom || ''} ${option.nom || ''}`}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Avatar sx={{ width: 32, height: 32, mr: 2 }}>
                      {option.prenom?.charAt(0)}{option.nom?.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2">{option.prenom} {option.nom}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.niveauFidelite || 'Standard'} • {option.email}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client"
                    placeholder="Sélectionner un client"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                value={selectedPackage}
                onChange={(event, newValue) => {
                  setSelectedPackage(newValue);
                  setFormData({...formData, package: newValue});
                  
                  // Auto-update destination and revenue if package is selected
                  if (newValue) {
                    setFormData(prev => ({
                      ...prev,
                      destination: newValue.destination,
                      revenue: newValue.prix * prev.participants
                    }));
                  }
                }}
                options={packages || []}
                getOptionLabel={(option) => option.titre || ''}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2">{option.titre}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.destination} • {formatCurrency(option.prix)} • {option.duree}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Forfait"
                    placeholder="Sélectionner un forfait"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Lieu/Destination"
                value={formData.destination}
                onChange={(e) => setFormData({...formData, destination: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Statut</InputLabel>
                <Select
                  value={formData.statut}
                  label="Statut"
                  onChange={(e) => setFormData({...formData, statut: e.target.value})}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="Planifié">Planifié</MenuItem>
                  <MenuItem value="Confirmé">Confirmé</MenuItem>
                  <MenuItem value="En attente">En attente</MenuItem>
                  <MenuItem value="Annulé">Annulé</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre de participants"
                type="number"
                value={formData.participants}
                onChange={(e) => {
                  const newParticipants = parseInt(e.target.value);
                  setFormData({
                    ...formData,
                    participants: newParticipants,
                    // Update revenue if package is selected
                    revenue: selectedPackage ? selectedPackage.prix * newParticipants : formData.revenue
                  });
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Revenu estimé"
                type="number"
                value={formData.revenue}
                onChange={(e) => setFormData({...formData, revenue: parseInt(e.target.value)})}
                disabled={!!selectedPackage}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                InputProps={{
                  startAdornment: (
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                      {settings.defaultCurrency}
                    </Typography>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Détails de l'événement..."
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleCloseDialog} sx={{ borderRadius: 2 }}>
            Annuler
          </Button>
          {editingEvent && (
            <Button 
              onClick={handleDeleteEvent}
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              sx={{ borderRadius: 2, mr: 'auto' }}
            >
              Supprimer
            </Button>
          )}
          <Button 
            onClick={handleSaveEvent}
            variant="contained"
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)'
            }}
          >
            {editingEvent ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({...snackbar, open: false})}
      >
        <Alert 
          onClose={() => setSnackbar({...snackbar, open: false})} 
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Calendrier; 